import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from '@mui/material';
import { getBreezeHistoricalChart, BreezeHistoricalChartParams } from '../../../services/request/BreezeWealth';
import BreezeCandlestickChart, { ForecastPoint, OhlcBar } from './BreezeCandlestickChart';

function breezeErr(e: unknown): string {
  const ax = e && typeof e === 'object' && 'response' in e ? (e as { response?: { data?: unknown } }).response : undefined;
  const d = ax?.data;
  if (typeof d === 'object' && d !== null && 'error' in d) {
    const er = (d as { error?: unknown }).error;
    if (typeof er === 'string') return er;
  }
  return '';
}

/** Map UI exchange (e.g. NSE+BSE) to API exchange. */
export function breezeChartApiExchange(exchangeLabel: string): string {
  const u = exchangeLabel.toUpperCase();
  if (u.includes('NSE')) return 'NSE';
  if (u.includes('BSE')) return 'BSE';
  return 'NSE';
}

export interface BreezeChartAnalysisModalProps {
  open: boolean;
  onClose: () => void;
  /** Pre-filled from holdings row */
  initialStockCode?: string;
  initialExchangeLabel?: string;
}

const BreezeChartAnalysisModal: React.FC<BreezeChartAnalysisModalProps> = ({
  open,
  onClose,
  initialStockCode,
  initialExchangeLabel,
}) => {
  const [stockCode, setStockCode] = useState('');
  const [exchangeCode, setExchangeCode] = useState('NSE');
  const [interval, setInterval] = useState('day');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [payload, setPayload] = useState<Record<string, unknown> | null>(null);

  const load = useCallback(
    async (override?: Partial<BreezeHistoricalChartParams>) => {
      const code = (override?.stockCode ?? stockCode).trim().toUpperCase();
      if (!code) {
        setError('Enter a stock code.');
        return;
      }
      const ex = override?.exchangeCode ?? exchangeCode;
      const int = override?.interval ?? interval;
      setLoading(true);
      setError(null);
      setPayload(null);
      try {
        const params: BreezeHistoricalChartParams = {
          stockCode: code,
          exchangeCode: ex,
          interval: int,
        };
        const res = await getBreezeHistoricalChart(params);
        setPayload(res.data as Record<string, unknown>);
      } catch (e: unknown) {
        setError(breezeErr(e) || 'Failed to load chart. Connect Breeze session and try again.');
      } finally {
        setLoading(false);
      }
    },
    [stockCode, exchangeCode, interval]
  );

  useEffect(() => {
    if (!open) {
      return;
    }
    setError(null);
    if (!initialStockCode?.trim()) {
      setPayload(null);
      return;
    }
    const sc = initialStockCode.trim().toUpperCase();
    const ex = breezeChartApiExchange(initialExchangeLabel ?? 'NSE');
    setStockCode(sc);
    setExchangeCode(ex);
    let cancelled = false;
    (async () => {
      setLoading(true);
      setPayload(null);
      try {
        const res = await getBreezeHistoricalChart({ stockCode: sc, exchangeCode: ex, interval });
        if (!cancelled) {
          setPayload(res.data as Record<string, unknown>);
        }
      } catch (e: unknown) {
        if (!cancelled) {
          setError(breezeErr(e) || 'Failed to load chart. Connect Breeze session and try again.');
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, initialStockCode, initialExchangeLabel, interval]);

  const bars = (payload?.bars as OhlcBar[] | undefined) ?? [];
  const analysis = payload?.analysis as Record<string, unknown> | undefined;
  const breezeErrTxt = payload?.breezeError as string | undefined;

  const forecast5 = analysis?.forecastNext5 as
    | { points?: ForecastPoint[]; summary?: string; disclaimer?: string; stepUnitLabel?: string }
    | undefined;
  const forecastPoints = Array.isArray(forecast5?.points) ? forecast5!.points : undefined;
  const predictionExtras = analysis?.predictionExtras as
    | {
        approxMacdBias?: string;
        bollinger?: Record<string, unknown>;
        swingLevels20?: Record<string, unknown>;
        volumeTrendNote?: string;
        predictionNotes?: string[];
      }
    | undefined;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth scroll="paper">
      <DialogTitle>Historical chart & simple signals</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          <>
          <Alert severity="info">
            Uses ICICI Breeze <strong>GetHistoricalCharts</strong> (signed v1 API). Hints are basic rules (averages, RSI-style
            math, a few candle shapes) — <strong>not</strong> investment advice.
          </Alert>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems="flex-start">
            <TextField
              label="Stock code"
              value={stockCode}
              onChange={(e) => setStockCode(e.target.value.toUpperCase())}
              size="small"
              sx={{ minWidth: 140 }}
            />
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>Exchange</InputLabel>
              <Select
                label="Exchange"
                value={exchangeCode}
                onChange={(e) => setExchangeCode(String(e.target.value))}
              >
                <MenuItem value="NSE">NSE</MenuItem>
                <MenuItem value="BSE">BSE</MenuItem>
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 160 }}>
              <InputLabel>Interval</InputLabel>
              <Select label="Interval" value={interval} onChange={(e) => setInterval(String(e.target.value))}>
                <MenuItem value="day">Daily</MenuItem>
                <MenuItem value="1minute">1 minute</MenuItem>
                <MenuItem value="5minute">5 minute</MenuItem>
                <MenuItem value="30minute">30 minute</MenuItem>
              </Select>
            </FormControl>
            <Button
              variant="contained"
              onClick={() => void load(undefined)}
              disabled={loading}
              sx={{ mt: { xs: 0, sm: 0.5 } }}
            >
              {loading ? <CircularProgress size={22} /> : 'Load'}
            </Button>
          </Stack>
          {error && <Alert severity="error">{error}</Alert>}
          {breezeErrTxt && <Alert severity="warning">ICICI: {breezeErrTxt}</Alert>}
          {payload?.apiNote && (
            <Typography variant="caption" color="text.secondary">
              {String(payload.apiNote)}
            </Typography>
          )}
          {bars.length > 0 && (
            <>
              <Typography variant="subtitle2">Candles and illustrative forward path</Typography>
              <BreezeCandlestickChart bars={bars} maxBars={80} forecastPoints={forecastPoints} />
            </>
          )}
          {forecast5 && (
            <>
              <Alert severity="warning" sx={{ mt: 1 }}>
                <strong>Next 5 steps</strong> ({String(forecast5.stepUnitLabel ?? 'bars')}) are a{' '}
                <strong>math sketch</strong> (trend line + volatility band), not a market prediction.{' '}
                {forecast5.disclaimer ? String(forecast5.disclaimer) : ''}
              </Alert>
              {forecast5.summary && (
                <Typography variant="body2" color="text.secondary">
                  {String(forecast5.summary)}
                </Typography>
              )}
              {forecastPoints && forecastPoints.length > 0 && (
                <TableContainer component={Paper} variant="outlined" sx={{ mt: 1 }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Step</TableCell>
                        <TableCell align="right">Projected close</TableCell>
                        <TableCell align="right">Band low</TableCell>
                        <TableCell align="right">Band high</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {forecastPoints.map((p) => (
                        <TableRow key={p.step}>
                          <TableCell>{p.step}</TableCell>
                          <TableCell align="right">{Number(p.projectedClose).toFixed(2)}</TableCell>
                          <TableCell align="right">{Number(p.bandLow).toFixed(2)}</TableCell>
                          <TableCell align="right">{Number(p.bandHigh).toFixed(2)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </>
          )}
          {predictionExtras && (
            <Box sx={{ mt: 1 }}>
              <>
              <Typography variant="subtitle2" gutterBottom>
                Extra indicators (simple)
              </Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 1 }}>
                <>
                {predictionExtras.approxMacdBias && (
                  <Chip size="small" label={`MACD-style: ${predictionExtras.approxMacdBias}`} variant="outlined" />
                )}
                {predictionExtras.bollinger?.position && (
                  <Chip size="small" label={`Bollinger: ${String(predictionExtras.bollinger.position)}`} />
                )}
                </>
              </Stack>
              {predictionExtras.swingLevels20 && (
                <Typography variant="body2" color="text.secondary" paragraph>
                  {String(predictionExtras.swingLevels20.label ?? 'Range')}: support ~{' '}
                  {String(predictionExtras.swingLevels20.supportApprox ?? '—')}, resistance ~{' '}
                  {String(predictionExtras.swingLevels20.resistanceApprox ?? '—')}
                </Typography>
              )}
              {predictionExtras.volumeTrendNote && (
                <Typography variant="body2" color="text.secondary" paragraph>
                  {predictionExtras.volumeTrendNote}
                </Typography>
              )}
              {Array.isArray(predictionExtras.predictionNotes) && predictionExtras.predictionNotes.length > 0 && (
                <Box component="ul" sx={{ pl: 2, m: 0 }}>
                  {predictionExtras.predictionNotes.map((line, i) => (
                    <li key={i}>
                      <Typography variant="body2">{line}</Typography>
                    </li>
                  ))}
                </Box>
              )}
              </>
            </Box>
          )}
          {analysis && (
            <Box>
              <>
              <Typography variant="subtitle2" gutterBottom>
                Plain-language ideas
              </Typography>
              {analysis.headline && (
                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  {String(analysis.headline)}
                </Typography>
              )}
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 1 }}>
                <>
                {analysis.primarySuggestion && (
                  <Chip size="small" color="primary" label={`Long view: ${String(analysis.primarySuggestion)}`} />
                )}
                {analysis.shortTermSuggestion && (
                  <Chip size="small" variant="outlined" label={`Short term: ${String(analysis.shortTermSuggestion)}`} />
                )}
                </>
              </Stack>
              {analysis.primaryLabel && (
                <Typography variant="body2" color="text.secondary" paragraph>
                  <strong>Longer horizon:</strong> {String(analysis.primaryLabel)}
                </Typography>
              )}
              {analysis.shortTermLabel && (
                <Typography variant="body2" color="text.secondary" paragraph>
                  <strong>Shorter horizon:</strong> {String(analysis.shortTermLabel)}
                </Typography>
              )}
              {Array.isArray(analysis.patternsDetected) && (analysis.patternsDetected as string[]).length > 0 && (
                <Typography variant="body2" sx={{ mb: 1 }}>
                  <strong>Patterns (simple):</strong> {(analysis.patternsDetected as string[]).join(' · ')}
                </Typography>
              )}
              {Array.isArray(analysis.plainEnglish) && (analysis.plainEnglish as string[]).length > 0 && (
                <Box component="ul" sx={{ pl: 2, m: 0 }}>
                  {(analysis.plainEnglish as string[]).map((line, i) => (
                    <li key={i}>
                      <Typography variant="body2">{line}</Typography>
                    </li>
                  ))}
                </Box>
              )}
              {analysis.disclaimer && (
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                  {String(analysis.disclaimer)}
                </Typography>
              )}
              </>
            </Box>
          )}
          </>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default BreezeChartAnalysisModal;
